import React, { useEffect, useState, useRef } from 'react';
import { Upload, Plus, Trash2, Lock, Image as ImageIcon, RefreshCw, GripVertical, ChevronDown, Pencil } from 'lucide-react';
import type { OnboardingStepProps } from '../onboardingSteps';
import {
  uploadImageFile,
  createImageRecord,
  fetchCategories,
  fetchImages,
  deleteImage,
  updateImage,
  reorderImages,
} from '../onboardingApi';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';
import { usePlan } from '@/context/PlanContext';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CategoryItem {
  _id: string;
  name: string;
  parentCategory?: { _id: string; name: string } | null;
}

interface ImageItem {
  _id: string;
  image: string;
  title: string;
  description?: string;
  category: string;
}

// ─── Sortable Image Row ──────────────────────────────────────

interface SortableImageRowProps {
  item: ImageItem;
  index: number;
  categories: CategoryItem[];
  onDelete: (id: string) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
  onEdit: (id: string, data: { title: string; description: string }) => Promise<void>;
}

function SortableImageRow({ item, index, categories, onDelete, onCategoryChange, onEdit }: SortableImageRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : undefined,
  };

  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editDescription, setEditDescription] = useState(item.description || '');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    if (catOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [catOpen]);

  const parentCategories = categories.filter(c => !c.parentCategory);
  const currentCat = categories.find(c => c._id === item.category);
  const catDisplayName = currentCat
    ? (currentCat.parentCategory ? `${currentCat.parentCategory.name} › ${currentCat.name}` : currentCat.name)
    : 'Uncategorized';

  return (
    <div ref={setNodeRef} style={style} className={`group ${isDragging ? 'shadow-lg rounded-lg' : ''}`}>
      <Card className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 transition-all ${isDragging ? 'ring-2 ring-primary/30' : ''}`}>
        {/* Drag handle */}
        <button
          className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground shrink-0 p-0.5"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Index badge */}
        <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-primary/10 text-primary">
          {index + 1}
        </div>

        {/* Thumbnail */}
        <div className="relative w-14 h-14 rounded-md overflow-hidden bg-muted shrink-0">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
        </div>

        {/* Category selector */}
        <div className="relative shrink-0 hidden sm:block" ref={catRef}>
          <button
            type="button"
            onClick={() => setCatOpen(!catOpen)}
            className="flex items-center gap-1 text-sm text-foreground bg-secondary hover:bg-accent rounded-md px-2 py-1 transition-colors max-w-25"
          >
            <span className="truncate">{catDisplayName}</span>
            <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
          </button>
          {catOpen && (
            <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-35 max-h-48 overflow-y-auto">
              {parentCategories.map(cat => (
                <button
                  key={cat._id}
                  type="button"
                  onClick={() => {
                    onCategoryChange(item._id, cat._id);
                    setCatOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                    item.category === cat._id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Edit */}
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-foreground shrink-0"
          onClick={() => {
            setEditTitle(item.title);
            setEditDescription(item.description || '');
            setEditing(true);
          }}
          aria-label={`Edit ${item.title}`}
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>

        {/* Delete */}
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-destructive shrink-0"
          onClick={() => onDelete(item._id)}
          aria-label={`Delete ${item.title}`}
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </Card>

      {/* Inline edit form */}
      {editing && (
        <Card className="mt-1.5 p-3 space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Live at Venue Name"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="A short caption for this image"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              loading={savingEdit}
              loadingText="Saving..."
              onClick={async () => {
                setSavingEdit(true);
                try {
                  await onEdit(item._id, { title: editTitle.trim(), description: editDescription.trim() });
                  setEditing(false);
                } finally {
                  setSavingEdit(false);
                }
              }}
            >
              Save
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

const MediaOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange, buildResult }) => {
  const { canAdd, openUpgradeModal, getUsage, refreshPlanInfo } = usePlan();
  const imageUsage = getUsage('images');

  // Existing images
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loadingImages, setLoadingImages] = useState(true);

  // Prefilled image from serper/Wikipedia (optimistic — shown until DB record loads or user deletes)
  const prefilledMedia = buildResult?.media ?? null;
  const [prefilledDismissed, setPrefilledDismissed] = useState(false);
  const [prefilledEditing, setPrefilledEditing] = useState(false);
  const [prefilledTitle, setPrefilledTitle] = useState('');
  const [prefilledDescription, setPrefilledDescription] = useState('');

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [imageTitle, setImageTitle] = useState('');
  const [imageDescription, setImageDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);

  // Category state
  const [allCategories, setAllCategories] = useState<CategoryItem[]>([]);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [parentDropdownOpen, setParentDropdownOpen] = useState(false);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);

  const parentDropdownRef = useRef<HTMLDivElement>(null);
  const subDropdownRef = useRef<HTMLDivElement>(null);

  const parentCategories = allCategories.filter((c) => !c.parentCategory);
  const subCategories = allCategories.filter(
    (c) => c.parentCategory && c.parentCategory._id === selectedParentId
  );
  const hasSubcategories = subCategories.length > 0;
  const finalCategoryId = hasSubcategories ? selectedSubId : selectedParentId;

  const limitReached = imageUsage ? !imageUsage.canAdd : false;

  useEffect(() => {
    if (loadingImages) return;
    const draftItem = showAddForm && uploadedImageUrl && finalCategoryId
      ? [{
          _id: 'onboarding-draft-image',
          image: uploadedImageUrl,
          title: imageTitle || 'New image',
          description: imageDescription,
          category: finalCategoryId,
          isActive: true,
        }]
      : [];
    onPreviewChange?.({ images: { items: [...draftItem, ...images], categories: allCategories } });
  }, [loadingImages, images, allCategories, showAddForm, uploadedImageUrl, imageTitle, imageDescription, finalCategoryId, onPreviewChange]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Load existing images + categories on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [existingImages, categories] = await Promise.all([
          fetchImages(),
          fetchCategories('image'),
        ]);
        setImages(Array.isArray(existingImages) ? existingImages : []);
        setAllCategories(Array.isArray(categories) ? (categories as CategoryItem[]) : []);
      } catch {
        // silent
      } finally {
        setLoadingImages(false);
      }
    };
    load();
  }, []);

  // Click-outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target as Node)) {
        setParentDropdownOpen(false);
      }
      if (subDropdownRef.current && !subDropdownRef.current.contains(event.target as Node)) {
        setSubDropdownOpen(false);
      }
    };
    if (parentDropdownOpen || subDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [parentDropdownOpen, subDropdownOpen]);

  // Reset subcategory when parent changes
  useEffect(() => {
    setSelectedSubId('');
  }, [selectedParentId]);

  // Clear category error once a category is selected
  useEffect(() => {
    if (finalCategoryId && error === 'Please select a category.') {
      setError('');
    }
  }, [finalCategoryId]);

  // Valid when: not busy AND (no form open, OR form open with complete data)
  useEffect(() => {
    if (isCropping || isUploading || saving) {
      // Busy — block Continue
      onValidationChange(false);
    } else if (showAddForm && uploadedImageUrl) {
      // Form is open with an uploaded image — require category before allowing Continue
      onValidationChange(!!finalCategoryId);
    } else {
      // Require at least one image — prefilled image counts toward this
      const hasPrefilled = !!prefilledMedia && !prefilledDismissed;
      onValidationChange(images.length > 0 || hasPrefilled);
    }
  }, [onValidationChange, showAddForm, uploadedImageUrl, finalCategoryId, isCropping, isUploading, saving, images.length, prefilledMedia, prefilledDismissed]);

  // Auto-add pending image on continue
  useEffect(() => {
    saveRef.current = async () => {
      if (showAddForm && uploadedImageUrl) {
        const catId = hasSubcategories ? selectedSubId : selectedParentId;
        if (catId) {
          try {
            const created = await createImageRecord({
              image: uploadedImageUrl,
              title: imageTitle.trim() || 'Gallery Image',
              category: catId,
              description: imageDescription.trim(),
            });
            setImages((prev) => [created, ...prev]);
            resetForm();
            refreshPlanInfo();
          } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to add image. Please try again.');
            throw err;
          }
        }
      }
    };
  }, [saveRef, showAddForm, uploadedImageUrl, imageTitle, imageDescription, selectedParentId, selectedSubId, hasSubcategories]);

  const resetForm = () => {
    setImageFile(null);
    setUploadedImageUrl('');
    setImageTitle('');
    setImageDescription('');
    setSelectedParentId('');
    setSelectedSubId('');
    setParentDropdownOpen(false);
    setSubDropdownOpen(false);
    setError('');
    setShowAddForm(false);
    setIsUploading(false);
    setIsCropping(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setError('File size exceeds 2 MB. Please choose a smaller image.');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Only PNG, JPG, and WebP files are supported.');
      return;
    }
    setImageFile(file);
    setIsCropping(true);
    setError('');
    // Auto-fill title from filename (without extension)
    if (!imageTitle) {
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setImageTitle(name);
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;
    setIsUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const imageUrl = await uploadImageFile(blob);
      setUploadedImageUrl(imageUrl);
      setIsCropping(false);
    } catch {
      setError('Image upload failed.');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadState = () => {
    setIsCropping(false);
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFileUpload = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (uploadedImageUrl) {
      fetch(uploadedImageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'image.png', { type: blob.type });
          setImageFile(file);
          setIsCropping(true);
        })
        .catch(() => {
          setError('Failed to load image for editing');
        });
    }
  };

  const handleAddImage = async () => {
    setError('');
    if (!uploadedImageUrl) {
      setError('Please upload an image.');
      return;
    }
    if (!finalCategoryId) {
      setError('Please select a category.');
      return;
    }

    setSaving(true);
    try {
      // Create image record (image already uploaded)
      const created = await createImageRecord({
        image: uploadedImageUrl,
        title: imageTitle.trim() || 'Gallery Image',
        category: finalCategoryId,
        description: imageDescription.trim(),
      });
      setImages((prev) => [created, ...prev]);
      resetForm();
      refreshPlanInfo();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add image.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (id: string) => {
    const prev = [...images];
    setImages((s) => s.filter((img) => img._id !== id));
    try {
      await deleteImage(id);
      refreshPlanInfo();
    } catch {
      setImages(prev); // revert on error
    }
  };

  const handleCategoryChange = async (imageId: string, categoryId: string) => {
    const item = images.find(i => i._id === imageId);
    if (!item) return;
    // Optimistic update
    setImages(prev => prev.map(i => i._id === imageId ? { ...i, category: categoryId } : i));
    try {
      await updateImage(imageId, { category: categoryId, image: item.image });
    } catch {
      // Revert on failure
      setImages(prev => prev.map(i => i._id === imageId ? { ...i, category: item.category } : i));
    }
  };

  const handleEditImage = async (imageId: string, data: { title: string; description: string }) => {
    const item = images.find(i => i._id === imageId);
    if (!item) return;
    // Optimistic update
    setImages(prev => prev.map(i => i._id === imageId ? { ...i, title: data.title, description: data.description } : i));
    try {
      await updateImage(imageId, { title: data.title, description: data.description, image: item.image });
    } catch {
      // Revert on failure
      setImages(prev => prev.map(i => i._id === imageId ? { ...i, title: item.title, description: item.description } : i));
    }
  };

  const handleAddClick = () => {
    if (!canAdd('images')) {
      openUpgradeModal('images');
      return;
    }
    setShowAddForm(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex(i => i._id === active.id);
    const newIndex = images.findIndex(i => i._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(images, oldIndex, newIndex);
    setImages(reordered);

    try {
      await reorderImages(reordered.map(i => ({ _id: i._id })));
    } catch { /* silent — order refreshes on next load */ }
  };

  return (
    <div className="space-y-5 onb-fade-in">
      {/* Header with limit badge */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Upload images to build your gallery.
        </p>
        {imageUsage && imageUsage.limit !== -1 && (
          <span className="text-xs font-medium text-muted-foreground bg-secondary px-2.5 py-1 rounded-full">
            {images.length} / {imageUsage.limit}
          </span>
        )}
      </div>

      {/* Add form */}
      {showAddForm ? (
        <Card className="p-4 space-y-4 border-dashed">
          {/* File upload zone / Cropper / Preview */}
          {isCropping && imageFile ? (
            /* Cropper step */
            <div className="space-y-2">
              <Card className="flex flex-col items-center">
                <div className="w-full border-b p-3">
                  <p className="text-sm font-medium text-foreground text-center">Adjust crop area - Drag to move, edges to resize</p>
                </div>
                <div className="w-full flex justify-center py-3">
                  <Cropper
                    file={imageFile}
                    aspect={undefined}
                    maxImageSize={2 * 1024 * 1024}
                    keepSelection
                    onCrop={handleCropComplete}
                    onCropRef={cropperRef}
                  />
                </div>
              </Card>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={async () => {
                    await cropperRef.current?.applyCrop();
                  }}
                  disabled={isUploading}
                  loading={isUploading}
                  loadingText="Uploading..."
                >
                  {uploadedImageUrl ? 'Update' : 'Upload'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={resetUploadState}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : uploadedImageUrl ? (
            /* Image preview (after upload) */
            <div className="space-y-2">
              <Card
                className="group flex max-h-70 sm:max-h-100 w-full flex-col items-center justify-center gap-4 py-5 sm:py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={openCropperForExisting}
              >
                <div 
                  className="relative max-h-64 max-w-64 rounded-2xl overflow-hidden"
                  style={{
                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 8px 8px',
                    backgroundRepeat: 'repeat'
                  }}
                >
                  <img 
                    src={uploadedImageUrl} 
                    alt="uploaded" 
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                    title="Click to edit image"
                  />
                </div>
                <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                  <Upload className="size-4" />
                  <span>Click to edit or re-upload</span>
                </div>
              </Card>
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={openFileUpload}
                >
                  <RefreshCw className="size-3.5" />
                  Change
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setUploadedImageUrl('')}
                >
                  <Trash2 className="size-3.5" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            /* Drop zone */
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-8 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('bg-muted/50');
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('bg-muted/50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('bg-muted/50');
                const file = e.dataTransfer.files[0];
                if (file) handleFileSelect(file);
              }}
            >
              <Upload className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Drop an image or click to browse</span>
              <span className="text-xs text-muted-foreground">PNG or JPG, max 2 MB</span>
            </div>
          )}
          {/* File error shown right below upload zone */}
          {!uploadedImageUrl && !isCropping && error && <p className="text-sm text-destructive">{error}</p>}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />

          {/* Title */}
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={imageTitle}
              onChange={(e) => setImageTitle(e.target.value)}
              placeholder="Live at Venue Name"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              value={imageDescription}
              onChange={(e) => setImageDescription(e.target.value)}
              placeholder="A short caption for this image"
            />
          </div>

          {/* Category selection */}
          <div className="space-y-1.5">
            <Label>
              Category <span className="text-red-500">*</span>
            </Label>
            {parentCategories.length > 0 ? (
              <div className="space-y-2">
                {/* Parent category dropdown */}
                <div className="relative" ref={parentDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setParentDropdownOpen(!parentDropdownOpen)}
                    className="px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between"
                  >
                    {selectedParentId
                      ? parentCategories.find((p) => p._id === selectedParentId)?.name || 'Select Category'
                      : 'Select Category'}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${parentDropdownOpen ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {parentDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-full max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedParentId('');
                          setParentDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          !selectedParentId
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        Select Category
                      </button>
                      {parentCategories.map((parent) => (
                        <button
                          key={parent._id}
                          type="button"
                          onClick={() => {
                            setSelectedParentId(parent._id);
                            setParentDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            selectedParentId === parent._id
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground hover:bg-muted/50'
                          }`}
                        >
                          {parent.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subcategory dropdown */}
                {hasSubcategories && (
                  <div className="relative" ref={subDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setSubDropdownOpen(!subDropdownOpen)}
                      className="px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between"
                    >
                      {selectedSubId
                        ? subCategories.find((s) => s._id === selectedSubId)?.name || 'Select Subcategory'
                        : 'Select Subcategory'}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`transition-transform ${subDropdownOpen ? 'rotate-180' : ''}`}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {subDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-full max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSubId('');
                            setSubDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            !selectedSubId
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground hover:bg-muted/50'
                          }`}
                        >
                          Select Subcategory
                        </button>
                        {subCategories.map((sub) => (
                          <button
                            key={sub._id}
                            type="button"
                            onClick={() => {
                              setSelectedSubId(sub._id);
                              setSubDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                              selectedSubId === sub._id
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No categories available. You can add them later from the dashboard.
              </p>
            )}
          </div>

          {/* Error */}
          {error && uploadedImageUrl && <p className="text-sm text-destructive">{error}</p>}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={handleAddImage} loading={saving} loadingText="Adding..." disabled={!uploadedImageUrl}>
              Add Image
            </Button>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : (
        /* Add Image trigger button */
        <Button
          type="button"
          variant="outline"
          onClick={handleAddClick}
          className="w-full rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 py-4 font-medium text-primary hover:text-primary"
        >
          {limitReached ? (
            <>
              <Lock className="w-4 h-4" />
              Upgrade to add more images
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add Image
            </>
          )}
        </Button>
      )}

      {/* Existing images list with drag-and-drop */}
      {loadingImages ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Prefilled image from web search (serper KG / Wikipedia) — shown until DB record loads or user dismisses */}
          {prefilledMedia && !prefilledDismissed && !images.some((img) => img.image === prefilledMedia.image) && (
            <div className="mb-3">
              <p className="text-[11px] text-muted-foreground px-1 mb-1.5">
                Auto-fetched from web search — delete if you don't want it
              </p>
              <Card className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-primary/5 border-primary/20">
                <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-primary/10 text-primary">
                  ★
                </div>
                <div className="relative w-14 h-14 rounded-md overflow-hidden bg-muted shrink-0">
                  <img
                    src={prefilledMedia.image}
                    alt={prefilledMedia.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{prefilledTitle || prefilledMedia.title || 'Artist photo'}</p>
                  <p className="text-xs text-muted-foreground">Auto-fetched</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => {
                    setPrefilledTitle(prefilledMedia.title || '');
                    setPrefilledDescription('');
                    setPrefilledEditing(true);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => setPrefilledDismissed(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </Card>
              {prefilledEditing && (
                <Card className="mt-2 p-3 space-y-3">
                  <div className="space-y-1.5">
                    <Label>Title</Label>
                    <Input
                      value={prefilledTitle}
                      onChange={(e) => setPrefilledTitle(e.target.value)}
                      placeholder="Live at Venue Name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      value={prefilledDescription}
                      onChange={(e) => setPrefilledDescription(e.target.value)}
                      placeholder="A short caption for this image"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => setPrefilledEditing(false)}>
                      Done
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPrefilledTitle(prefilledMedia.title || '');
                        setPrefilledDescription('');
                        setPrefilledEditing(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          )}

          {images.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground px-1">
                Drag images to reorder
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={images.map(i => i._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1.5">
                    {images.map((item, index) => (
                      <SortableImageRow
                        key={item._id}
                        item={item}
                        index={index}
                        categories={allCategories}
                        onDelete={handleDeleteImage}
                        onCategoryChange={handleCategoryChange}
                        onEdit={handleEditImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default MediaOnboarding;
